import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarPopup } from './CalendarPopup.tsx'

/**
 * ══ Ô NHẬP NGÀY — gõ `dd/mm/yyyy` ════════════════════════════════════════════════════════════
 *
 * ⚠ **CẤM `<input type="date">`** (CLAUDE.md §3). Nó hiển thị theo LOCALE CỦA MÁY người dùng —
 *   máy đặt tiếng Anh thì ô ngày hiện `mm/dd/yyyy`, và người dùng Việt gõ `03/09` tưởng là 3 tháng
 *   9 trong khi trình duyệt hiểu là 9 tháng 3. Không có cách nào ép định dạng cho nó.
 *
 * ⚠ Giá trị ra/vào là `YYYY-MM-DD` — CHUỖI, không phải `Date`. Không đi qua `Date` là không có
 *   chỗ nào cho múi giờ máy chen vào (§3: ngày neo `Asia/Ho_Chi_Minh`).
 *
 * ⭐ LỊCH BUNG RA đã dựng 18/09/2026 (`CalendarPopup.tsx`) — trước đó cố ý bỏ, nhưng đợt soi
 *   17–18/09 cho thấy nó thiếu ở MỌI màn có ô ngày. Hai điều kiện đã nêu lúc hoãn đều được giữ:
 *   popup đi qua PORTAL nên modal không cắt, và bấm ra ngoài / Esc đều đóng đúng nó.
 *
 * ⭐ GÕ SỐ KHÔNG THÔI CŨNG RA NGÀY: `18092026` tự thành `18/09/2026` (bản cũ `ui.tsx:544-551`).
 *   Bắt người dùng gõ dấu `/` giữa các số là thêm ba lần bấm cho mỗi ô ngày trong ngày làm việc.
 */
const pad = (n: number): string => String(n).padStart(2, '0')

/** `2026-09-08` → `08/09/2026`. Chuỗi rỗng/không hợp lệ → rỗng. */
export const isoToVN = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m === null ? '' : `${m[3]}/${m[2]}/${m[1]}`
}

/** `08/09/2026` → `2026-09-08`, và KIỂM ngày có thật (31/02 ⇒ `null`). */
export const vnToIso = (s: string): string | null => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s.trim())
  if (m === null) return null
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])]
  if (mo < 1 || mo > 12 || d < 1) return null
  // Số ngày thật của tháng — `new Date(y, mo, 0)` cho ngày cuối tháng, KHÔNG phụ thuộc múi giờ vì
  // chỉ dùng phần lịch, không lấy mốc thời gian ra.
  if (d > new Date(y, mo, 0).getDate()) return null
  return `${y}-${pad(mo)}-${pad(d)}`
}

/** `18092026` → `18/09/2026`; gõ dở thì chèn dấu tới đâu hay tới đó. */
export const autoSlash = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return digits
}

export const DateInput = ({ valueIso, onChangeIso, disabled, ariaLabel, className = '' }: {
  valueIso: string
  onChangeIso: (iso: string) => void
  disabled?: boolean
  ariaLabel?: string
  /** Nối vào lớp của Ô NHẬP (không phải thẻ bọc) — y `DateInput` bản cũ (`components/ui.tsx:490`), để màn ép cỡ bằng `!w-24 !py-1 …`. */
  className?: string
}): JSX.Element => {
  const { t } = useTranslation()
  const display = isoToVN(valueIso)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const popRef = useRef<HTMLDivElement>(null)

  /* Bấm ra ngoài thì đóng lịch. Popup nằm ở `document.body` (portal) nên phải soi CẢ ref của nó —
     soi mỗi thẻ bọc là bấm vào chính lịch cũng bị tính là "ra ngoài". */
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node
      if (wrapRef.current?.contains(target) === true || popRef.current?.contains(target) === true) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [open])

  // ⚠ KHÔNG dùng `useEffect` để đồng bộ ô hiển thị với giá trị bên ngoài: luật lint
  //   `react-hooks/set-state-in-effect` chặn (quyết định B1), và đúng ra thì đây là GIÁ TRỊ SUY RA
  //   chứ không phải trạng thái cần đồng bộ. Đang gõ thì lấy chuỗi người dùng gõ; không gõ thì lấy
  //   thẳng giá trị bên ngoài — một dòng, không hiệu ứng phụ, không lệch nhịp.
  const shown = editing ? text : display

  /* ⚠ `inline-block`, KHÔNG `block` — chép `components/ui.tsx:524` bản cũ. `block` + ô `w-full` thì trong
     một hàng flex (thanh lọc) ô bị flex bóp còn ~60 px trên điện thoại ("01, 📅"), còn `inline-block` giữ
     bề rộng tự nhiên (~175 px) hoặc đúng bề rộng màn ép qua `className` (soi mobile 12/10/2026). */
  return (
    <span ref={wrapRef} className="relative inline-block">
      <input
        type="text" inputMode="numeric" placeholder="dd/mm/yyyy" maxLength={10}
        value={shown} disabled={disabled} aria-label={ariaLabel}
        onFocus={() => { setText(display); setEditing(true) }}
        onChange={(e) => {
          const formatted = autoSlash(e.target.value)
          setText(formatted)
          // Chỉ áp dụng khi gõ ĐỦ một ngày hợp lệ — gõ dở "08/0" không được xoá giá trị đang có.
          const iso = vnToIso(formatted)
          if (iso !== null) onChangeIso(iso)
        }}
        onBlur={() => { setEditing(false) }}
        className={`w-full rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 ${className}`}
      />
      <button
        type="button" tabIndex={-1} disabled={disabled} aria-label={t('common.pickDate')}
        onClick={() => {
          if (disabled === true) return
          if (open) { setOpen(false); return }
          setAnchor(wrapRef.current?.getBoundingClientRect() ?? null)
          setOpen(true)
        }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 leading-none text-slate-400 hover:text-brand-600">
        📅
      </button>
      {open && (
        <CalendarPopup
          valueIso={valueIso} anchorRect={anchor} popRef={popRef}
          todayLabel={t('common.today')}
          onPick={(iso) => { onChangeIso(iso); setText(isoToVN(iso)); setEditing(false); setOpen(false) }}
          onClose={() => { setOpen(false) }} />
      )}
    </span>
  )
}
